import {
  fetchInventoryValuation,
  fetchMenuItemProfitability,
  fetchTheoreticalVsActual,
  type InventoryValuationRow,
  type MenuItemProfitabilityRow,
  type TheoVsActualRow,
} from '@/renderer/services/inventoryReportsService';
import { apiError } from '@/renderer/util/notify';
import { Button, DatePicker, EmptyState, TabButton } from '@components/common';
import { useAppSelector } from '@hooks/useRedux';
import { selectCurrentUser } from '@store/auth/authSelectors';
import { selectEffectiveOutletId } from '@store/outlets';
import { BarChart2, BookOpen, Package, TrendingUp } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';

type ReportTab = 'valuation' | 'theo-vs-actual' | 'profitability';

const TABS: {
  id: ReportTab;
  label: string;
  icon: React.FC<{ size?: number }>;
}[] = [
  { id: 'valuation', label: 'Stock Valuation', icon: Package },
  { id: 'theo-vs-actual', label: 'Theoretical vs Actual', icon: TrendingUp },
  { id: 'profitability', label: 'Menu Profitability', icon: BookOpen },
];

const fmt = (n: number) =>
  n.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtPct = (n: number) => `${n.toFixed(1)}%`;

function KpiCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
        {label}
      </p>
      <p className="mt-1 text-2xl font-bold text-gray-900">{value}</p>
      {sub ? <p className="mt-0.5 text-xs text-gray-400">{sub}</p> : null}
    </div>
  );
}

function ValuationReport({
  companyId,
  outletId,
}: {
  companyId: number;
  outletId: string | null;
}) {
  const [rows, setRows] = useState<InventoryValuationRow[]>([]);
  const [summary, setSummary] = useState({
    totalValue: 0,
    totalItems: 0,
    totalLocations: 0,
  });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchInventoryValuation(companyId, outletId);
      setRows(result.rows);
      setSummary(result.summary);
    } catch {
      apiError('Failed to load stock valuation');
    } finally {
      setLoading(false);
    }
  }, [companyId, outletId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-3 gap-4">
        <KpiCard
          label="Total stock value"
          value={fmt(summary.totalValue)}
          sub="Weighted average cost"
        />
        <KpiCard label="Distinct items" value={String(summary.totalItems)} />
        <KpiCard label="Locations" value={String(summary.totalLocations)} />
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
          <h3 className="text-sm font-semibold text-gray-800">
            Stock by location
          </h3>
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            isLoading={loading}
          >
            Refresh
          </Button>
        </div>
        <div className="h-[30rem] overflow-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 border-b border-gray-100 bg-gray-50">
              <tr className="text-left">
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Item
                </th>
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Location
                </th>
                <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Qty on hand
                </th>
                <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Avg cost
                </th>
                <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Total value
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-5 py-10 text-center text-sm text-gray-400"
                  >
                    {loading ? 'Loading…' : 'No stock on hand'}
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr
                    key={`${row.inventory_item_id}-${row.stock_location_id}`}
                    className="border-t border-gray-100"
                  >
                    <td className="px-5 py-3 font-medium text-gray-900">
                      {row.item_name}
                      {row.item_code ? (
                        <span className="ml-1.5 text-xs text-gray-400">
                          {row.item_code}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-5 py-3 text-gray-600">
                      {row.location_name}
                    </td>
                    <td className="px-5 py-3 text-right font-mono text-gray-700">
                      {fmt(row.quantity_on_hand)}
                    </td>
                    <td className="px-5 py-3 text-right font-mono text-gray-700">
                      {fmt(row.average_cost)}
                    </td>
                    <td className="px-5 py-3 text-right font-mono font-semibold text-gray-900">
                      {fmt(row.total_value)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function TheoreticalVsActualReport({
  companyId,
  outletId,
}: {
  companyId: number;
  outletId: string | null;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000)
    .toISOString()
    .slice(0, 10);
  const [dateFrom, setDateFrom] = useState(thirtyDaysAgo);
  const [dateTo, setDateTo] = useState(today);
  const [rows, setRows] = useState<TheoVsActualRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasRun, setHasRun] = useState(false);

  const run = useCallback(async () => {
    if (!outletId) return;
    setLoading(true);
    try {
      const result = await fetchTheoreticalVsActual(
        companyId,
        outletId,
        dateFrom,
        dateTo,
      );
      setRows(result);
      setHasRun(true);
    } catch {
      apiError('Failed to load usage report');
    } finally {
      setLoading(false);
    }
  }, [companyId, outletId, dateFrom, dateTo]);

  const totalVarianceValue = rows.reduce((s, r) => s + r.variance_value, 0);
  const overUsed = rows.filter((r) => r.variance > 0).length;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-gray-200 bg-white p-4">
        <DatePicker
          label="From"
          mode="single"
          value={dateFrom}
          onChange={setDateFrom}
          placeholder="Start date"
        />
        <DatePicker
          label="To"
          mode="single"
          value={dateTo}
          onChange={setDateTo}
          placeholder="End date"
        />
        <Button onClick={run} isLoading={loading}>
          Run report
        </Button>
      </div>

      {hasRun && (
        <>
          <div className="grid grid-cols-3 gap-4">
            <KpiCard label="Items with variance" value={String(rows.length)} />
            <KpiCard label="Over-consumed items" value={String(overUsed)} />
            <KpiCard
              label="Total variance value"
              value={fmt(totalVarianceValue)}
              sub="At average cost"
            />
          </div>

          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
            <div className="h-[28rem] overflow-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 border-b border-gray-100 bg-gray-50">
                  <tr className="text-left">
                    <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Item
                    </th>
                    <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Theoretical
                    </th>
                    <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Actual
                    </th>
                    <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Variance
                    </th>
                    <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Var %
                    </th>
                    <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Value at risk
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-5 py-10 text-center text-sm text-gray-400"
                      >
                        No sale consumption movements in this period
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => {
                      const isOver = row.variance > 0;
                      return (
                        <tr
                          key={row.inventory_item_id}
                          className="border-t border-gray-100"
                        >
                          <td className="px-5 py-3 font-medium text-gray-900">
                            {row.item_name}
                          </td>
                          <td className="px-5 py-3 text-right font-mono text-gray-700">
                            {fmt(row.theoretical_usage)}
                          </td>
                          <td className="px-5 py-3 text-right font-mono text-gray-700">
                            {fmt(row.actual_usage)}
                          </td>
                          <td
                            className={`px-5 py-3 text-right font-mono font-semibold ${isOver ? 'text-red-600' : 'text-emerald-600'}`}
                          >
                            {isOver ? '+' : ''}
                            {fmt(row.variance)}
                          </td>
                          <td
                            className={`px-5 py-3 text-right text-xs font-semibold ${isOver ? 'text-red-500' : 'text-emerald-500'}`}
                          >
                            {isOver ? '+' : ''}
                            {fmtPct(row.variance_pct)}
                          </td>
                          <td className="px-5 py-3 text-right font-mono text-gray-700">
                            {fmt(row.variance_value)}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {!hasRun && !loading && (
        <EmptyState
          title="Select a date range"
          description="Choose a from and to date, then click Run report to compare theoretical vs actual ingredient consumption."
        />
      )}
    </div>
  );
}

function ProfitabilityReport({
  companyId,
  outletId,
}: {
  companyId: number;
  outletId: string | null;
}) {
  const [rows, setRows] = useState<MenuItemProfitabilityRow[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchMenuItemProfitability(companyId, outletId);
      setRows(result);
    } catch {
      apiError('Failed to load profitability data');
    } finally {
      setLoading(false);
    }
  }, [companyId, outletId]);

  useEffect(() => {
    load();
  }, [load]);

  const withRecipe = rows.filter((r) => r.has_recipe);
  const avgFoodCost =
    withRecipe.length > 0
      ? withRecipe.reduce((s, r) => s + r.food_cost_pct, 0) / withRecipe.length
      : 0;
  const topItem = withRecipe[0];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-3 gap-4">
        <KpiCard
          label="Menu items"
          value={String(rows.length)}
          sub={`${withRecipe.length} with recipes`}
        />
        <KpiCard
          label="Avg food cost %"
          value={fmtPct(avgFoodCost)}
          sub="Items with recipes only"
        />
        {topItem ? (
          <KpiCard
            label="Highest GP item"
            value={fmt(topItem.gross_profit)}
            sub={topItem.menu_item_name}
          />
        ) : (
          <KpiCard label="Highest GP item" value="—" />
        )}
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
          <h3 className="text-sm font-semibold text-gray-800">
            Menu item profitability
          </h3>
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            isLoading={loading}
          >
            Refresh
          </Button>
        </div>
        <div className="h-[30rem] overflow-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 border-b border-gray-100 bg-gray-50">
              <tr className="text-left">
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Menu item
                </th>
                <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Selling price
                </th>
                <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Recipe cost
                </th>
                <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Gross profit
                </th>
                <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Food cost %
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-5 py-10 text-center text-sm text-gray-400"
                  >
                    {loading ? 'Loading…' : 'No menu items found'}
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const costPctColor =
                    row.food_cost_pct === 0
                      ? 'text-gray-400'
                      : row.food_cost_pct < 30
                        ? 'text-emerald-600'
                        : row.food_cost_pct < 40
                          ? 'text-amber-600'
                          : 'text-red-600';
                  return (
                    <tr
                      key={row.menu_item_id}
                      className="border-t border-gray-100"
                    >
                      <td className="px-5 py-3">
                        <span className="font-medium text-gray-900">
                          {row.menu_item_name}
                        </span>
                        {!row.has_recipe && (
                          <span className="ml-2 text-xs text-gray-400 italic">
                            No recipe
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-right font-mono text-gray-700">
                        {fmt(row.selling_price)}
                      </td>
                      <td className="px-5 py-3 text-right font-mono text-gray-700">
                        {row.has_recipe ? (
                          fmt(row.recipe_cost)
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td
                        className={`px-5 py-3 text-right font-mono font-semibold ${row.gross_profit >= 0 ? 'text-gray-900' : 'text-red-600'}`}
                      >
                        {row.has_recipe ? (
                          fmt(row.gross_profit)
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td
                        className={`px-5 py-3 text-right text-xs font-semibold ${costPctColor}`}
                      >
                        {row.has_recipe ? (
                          fmtPct(row.food_cost_pct)
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export const InventoryReportsPage: React.FC = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const companyId = currentUser?.company?.id ?? null;
  const outletId = useAppSelector(selectEffectiveOutletId);
  const [activeTab, setActiveTab] = useState<ReportTab>('valuation');

  if (!companyId) {
    return (
      <EmptyState
        title="No company context"
        description="Unable to load inventory reports without a company context."
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex items-center gap-3">
          <BarChart2 size={20} className="text-emerald-600" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Inventory reports
            </h2>
            <p className="text-sm text-gray-500">
              Valuation, usage variance, and menu profitability
            </p>
          </div>
        </div>
        <div className="mt-4 flex gap-5 overflow-x-auto border-b border-gray-100">
          {TABS.map((tab) => (
            <TabButton
              key={tab.id}
              label={tab.label}
              isActive={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
            />
          ))}
        </div>
      </div>

      {activeTab === 'valuation' && (
        <ValuationReport companyId={companyId} outletId={outletId} />
      )}
      {activeTab === 'theo-vs-actual' && (
        <TheoreticalVsActualReport companyId={companyId} outletId={outletId} />
      )}
      {activeTab === 'profitability' && (
        <ProfitabilityReport companyId={companyId} outletId={outletId} />
      )}
    </div>
  );
};
